import React, { useEffect, useMemo, useState } from "react";
import api from "../../../api";
import swal from "sweetalert";
import usePermission from "../../../hooks/usePermission";

const RolePermissions = () => {
 const [loading, setLoading] = useState(true);
 const [users, setUsers] = useState([]);
 const [permissions, setPermissions] = useState([]);
 const [roles, setRoles] = useState([]);
 const [matrix, setMatrix] = useState({});
 const [activeMap, setActiveMap] = useState({});
 const [selectedUserId, setSelectedUserId] = useState("");
 const [searchTerm, setSearchQuery] = useState("");
 const [permSearch, setPermSearch] = useState("");
 const { hasPermission } = usePermission();
 const canEditPermissions = hasPermission("edit:permissions");

 const formatText = (value = "") =>
 value.replace(/[-_]/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

 const parsePermission = (name = "") => {
 const [action, ...moduleParts] = name.includes(":") ? name.split(":") : ["other", name];
 return {
 action: action || "other",
 module: moduleParts.join(":") || name || "other",
 };
 };

 const actionOrder = ["read", "view", "create", "edit", "update", "approve", "reject", "delete", "manage", "download", "upload", "export", "other"];

 const filteredUsers = useMemo(() => {
 const query = searchTerm.trim().toLowerCase();
 return users.filter((user) => {
 const roleName = user.role?.name || "";
 return !query || user.name?.toLowerCase().includes(query) || roleName.toLowerCase().includes(query);
 });
 }, [users, searchTerm]);

 const selectedUser = users.find((user) => user._id === selectedUserId) || filteredUsers[0] || users[0];
 const selectedPermissions = matrix[selectedUser?._id] || [];
 const selectedRole = roles.find((role) => role._id === selectedUser?.role?._id);
 const inheritedPermissions = selectedRole?.permissions?.map((permission) => permission.name) || [];

 const groupedPermissions = useMemo(() => {
 const query = permSearch.trim().toLowerCase();

 return permissions
 .filter((permission) => !query || permission.name.toLowerCase().includes(query))
 .reduce((acc, permission) => {
 const parsed = parsePermission(permission.name);
 if (!acc[parsed.module]) acc[parsed.module] = [];
 acc[parsed.module].push({ ...permission, ...parsed });
 return acc;
 }, {});
 }, [permissions, permSearch]);

 const sortedGroups = useMemo(
 () =>
 Object.entries(groupedPermissions)
 .map(([module, perms]) => [
 module,
 perms.sort((a, b) => {
 const actionA = actionOrder.indexOf(a.action);
 const actionB = actionOrder.indexOf(b.action);
 return (actionA === -1 ? 99 : actionA) - (actionB === -1 ? 99 : actionB);
 }),
 ])
 .sort(([a], [b]) => a.localeCompare(b)),
 [groupedPermissions]
 );

 const loadUsers = async () => {
 const res = await api.get("/users?limit=999");
 return res.data.data || [];
 };

 const loadPermissions = async () => {
 const res = await api.get("/permissions?limit=999");
 return res.data.data || [];
 };

 const loadRoles = async () => {
 const res = await api.get("/roles?limit=999");
 return res.data.data || [];
 };

 const loadUserPermissions = async () => {
 try {
 const res = await api.get("/user-permissions");
 return res.data.data || [];
 } catch (err) {
 console.error("User permissions load error:", err);
 return [];
 }
 };

 const buildMatrix = (userList, roleList, userPermissions) => {
 const result = {};
 const active = {};

 userList.forEach((user) => {
 const userPermission = userPermissions.find((item) => item.user?._id === user._id);
 const role = roleList.find((item) => item._id === user.role?._id);
 const rolePerms = role ? role.permissions.map((permission) => permission.name) : [];

 if (userPermission) {
 // New delta format
 const added = userPermission.added || [];
 const removed = userPermission.removed || [];
 // hasNewFormat = true even when both are empty (doc exists = intentional state)
 const hasNewFormat = userPermission.added !== undefined || userPermission.removed !== undefined;

 if (hasNewFormat) {
 // Apply delta: rolePerms + added - removed
 const finalPerms = [...new Set([...rolePerms, ...added])].filter(p => !removed.includes(p));
 result[user._id] = finalPerms;
 } else if (Array.isArray(userPermission.permissions) && userPermission.permissions.length > 0) {
 // Old format fallback
 result[user._id] = userPermission.permissions;
 } else {
 // No override — use role
 result[user._id] = rolePerms;
 }
 active[user._id] = userPermission.status !== "Inactive";
 } else {
 result[user._id] = rolePerms;
 active[user._id] = user.status !== "Inactive";
 }
 });

 setActiveMap(active);
 return result;
 };

 useEffect(() => {
 const loadData = async () => {
 try {
 setLoading(true);
 const [loadedUsers, loadedPermissions, loadedRoles, loadedUserPermissions] = await Promise.all([
 loadUsers(),
 loadPermissions(),
 loadRoles(),
 loadUserPermissions(),
 ]);

 setUsers(loadedUsers);
 setPermissions(loadedPermissions);
 setRoles(loadedRoles);
 setMatrix(buildMatrix(loadedUsers, loadedRoles, loadedUserPermissions));
 setSelectedUserId(loadedUsers[0]?._id || "");
 } catch (err) {
 console.error("Load error:", err);
 swal("Error", "Failed to load data", "error");
 } finally {
 setLoading(false);
 }
 };

 loadData();
 }, []);

 useEffect(() => {
 if (!filteredUsers.some((user) => user._id === selectedUserId)) {
 setSelectedUserId(filteredUsers[0]?._id || "");
 }
 }, [filteredUsers, selectedUserId]);

 const togglePermission = (userId, permName) => {
 setMatrix((prev) => {
 const current = prev[userId] || [];
 return {
 ...prev,
 [userId]: current.includes(permName)
 ? current.filter((permission) => permission !== permName)
 : [...current, permName],
 };
 });
 };

 const toggleModule = (modulePermissions) => {
 if (!selectedUser) return;

 const names = modulePermissions.map((permission) => permission.name);
 const allSelected = names.every((name) => selectedPermissions.includes(name));

 setMatrix((prev) => ({
 ...prev,
 [selectedUser._id]: allSelected
 ? (prev[selectedUser._id] || []).filter((name) => !names.includes(name))
 : Array.from(new Set([...(prev[selectedUser._id] || []), ...names])),
 }));
 };

 const toggleActive = (userId) => {
 setActiveMap((prev) => ({
 ...prev,
 [userId]: !prev[userId],
 }));
 };

 const selectAllForUser = () => {
 if (!selectedUser) return;
 setMatrix((prev) => ({
 ...prev,
 [selectedUser._id]: permissions.map((permission) => permission.name),
 }));
 };

 const clearForUser = () => {
 if (!selectedUser) return;
 setMatrix((prev) => ({ ...prev, [selectedUser._id]: [] }));
 };

 const restoreRoleDefaults = () => {
 if (!selectedUser) return;
 setMatrix((prev) => ({ ...prev, [selectedUser._id]: inheritedPermissions }));
 };

 const handleSave = async () => {
 swal({
 title: "Save Changes?",
 text: "Apply permission and status changes for all users?",
 icon: "warning",
 buttons: ["Cancel", "Save"],
 }).then(async (confirm) => {
 if (!confirm) return;

 try {
 const updates = Object.keys(matrix).map((userId) => {
 const user = users.find(u => u._id === userId);
 const userRole = roles.find(r => r._id === user?.role?._id);
 const rolePerms = (userRole?.permissions || []).map(p => p.name);

 return {
 userId,
 permissions: matrix[userId],
 rolePermissions: rolePerms,
 status: activeMap[userId] ? "Active" : "Inactive",
 };
 });

 await api.post("/user-permissions/bulk-update", { updates });
 swal("Success", "Permissions updated successfully!", "success");
 } catch (err) {
 swal("Error", "Failed to save changes", "error");
 }
 });
 };

 const sensitiveCount = selectedPermissions.filter((name) => /(delete|manage|export|approve)/i.test(name)).length;

 if (loading) {
 return (
 <div className="card shadow-sm border-0">
 <div className="text-center py-5">
 <div className="spinner-border text-primary spinner-border-sm" role="status"></div>
 <div className="mt-2 text-muted fs-14">Loading access settings...</div>
 </div>
 </div>
 );
 }

 return (
 <div className="access-page">
 <div className="access-toolbar">
 <div>
 <h4 className="mb-1 text-dark fw-bold">User Permissions</h4>
 <div className="text-muted fs-13">Select a user, review role defaults, then adjust permissions.</div>
 </div>
 {canEditPermissions && (
 <button onClick={handleSave} className="btn btn-primary px-4 shadow-sm">
 <i className="fa fa-save me-2"></i>Save Changes
 </button>
 )}
 </div>

 <div className="access-layout">
 <aside className="user-panel">
 <div className="input-group input-group-sm mb-3">
 <span className="input-group-text bg-light border-end-0">
 <i className="fa fa-search text-muted"></i>
 </span>
 <input
 type="text"
 className="form-control form-control-sm bg-light border-start-0"
 placeholder="Search user or role..."
 value={searchTerm}
 onChange={(e) => setSearchQuery(e.target.value)}
 />
 </div>

 <div className="user-list">
 {filteredUsers.map((user) => {
 const isSelected = user._id === selectedUser?._id;
 const permissionCount = matrix[user._id]?.length || 0;

 return (
 <button
 type="button"
 key={user._id}
 className={`user-item ${isSelected ? "active" : ""}`}
 onClick={() => setSelectedUserId(user._id)}
 >
 <span className="user-avatar">{(user.name || "U").charAt(0).toUpperCase()}</span>
 <span className="user-copy">
 <span className="user-name">{user.name}</span>
 <span className="user-meta">{user.role?.name || "No Role"} · {permissionCount} permissions</span>
 </span>
 <span className={`status-dot ${activeMap[user._id] ? "on" : "off"}`}></span>
 </button>
 );
 })}

 {filteredUsers.length === 0 && (
 <div className="text-center text-muted py-4 fs-13">No users found.</div>
 )}
 </div>
 </aside>

 <main className="permission-panel">
 {selectedUser ? (
 <>
 <div className="selected-user-header">
 <div className="selected-user-info">
 <span className="selected-avatar">{(selectedUser.name || "U").charAt(0).toUpperCase()}</span>
 <div className="selected-user-copy">
 <h5 className="mb-1 text-dark fw-bold">{selectedUser.name}</h5>
 <div className="selected-badges">
 <span className="access-badge primary">{selectedUser.role?.name || "No Role"}</span>
 <span className="access-badge">{selectedPermissions.length} selected</span>
 {sensitiveCount > 0 && <span className="access-badge warning">{sensitiveCount} sensitive</span>}
 </div>
 </div>
 </div>

 <div className="selected-actions">
 <label className="switch-row mb-0">
 <input
 type="checkbox"
 checked={Boolean(activeMap[selectedUser._id])}
 onChange={() => toggleActive(selectedUser._id)}
 />
 <span>{activeMap[selectedUser._id] ? "Active" : "Inactive"}</span>
 </label>
 <button type="button" className="btn btn-xs btn-outline-primary" onClick={restoreRoleDefaults}>
 Role Defaults
 </button>
 <button type="button" className="btn btn-xs btn-outline-primary" onClick={selectAllForUser}>
 Select All
 </button>
 <button type="button" className="btn btn-xs btn-outline-secondary" onClick={clearForUser}>
 Clear
 </button>
 </div>
 </div>

 <div className="permission-filter-row">
 <div className="input-group input-group-sm">
 <span className="input-group-text bg-light border-end-0">
 <i className="fa fa-lock text-muted"></i>
 </span>
 <input
 type="text"
 className="form-control form-control-sm bg-light border-start-0"
 placeholder="Search permissions, e.g. billing, delete, approve..."
 value={permSearch}
 onChange={(e) => setPermSearch(e.target.value)}
 />
 </div>
 <div className={`risk-note ${sensitiveCount > 0 ? "warn" : ""}`}>
 <i className={`fa ${sensitiveCount > 0 ? "fa-exclamation-triangle" : "fa-check-circle"} me-2`}></i>
 {sensitiveCount > 0 ? "Review sensitive access before saving" : "No sensitive access selected"}
 </div>
 </div>

 <div className="permission-module-list">
 {sortedGroups.map(([module, modulePermissions]) => {
 const selectedInModule = modulePermissions.filter((permission) => selectedPermissions.includes(permission.name)).length;
 const allSelected = selectedInModule === modulePermissions.length;
 const someSelected = selectedInModule > 0 && !allSelected;

 return (
 <section className="permission-module" key={module}>
 <div className="module-head">
 <button
 type="button"
 className={`module-toggle ${allSelected ? "active" : ""} ${someSelected ? "partial" : ""}`}
 onClick={() => toggleModule(modulePermissions)}
 disabled={!activeMap[selectedUser._id]}
 >
 <i className={`fa ${allSelected ? "fa-check" : someSelected ? "fa-minus" : "fa-plus"}`}></i>
 </button>
 <div>
 <div className="module-title">{formatText(module)}</div>
 <div className="text-muted fs-12">{selectedInModule}/{modulePermissions.length} selected</div>
 </div>
 </div>

 <div className="permission-pills">
 {modulePermissions.map((permission) => {
 const checked = selectedPermissions.includes(permission.name);
 const inherited = inheritedPermissions.includes(permission.name);
 const sensitive = /(delete|manage|export|approve)/i.test(permission.name);

 return (
 <button
 type="button"
 key={permission._id}
 className={`permission-pill ${checked ? "active" : ""} ${sensitive ? "sensitive" : ""}`}
 onClick={() => togglePermission(selectedUser._id, permission.name)}
 disabled={!activeMap[selectedUser._id]}
 title={permission.name}
 >
 <span>{formatText(permission.action)}</span>
 {inherited && <i className="fa fa-link inherited-icon"></i>}
 {sensitive && <i className="fa fa-shield-alt"></i>}
 </button>
 );
 })}
 </div>
 </section>
 );
 })}

 {sortedGroups.length === 0 && (
 <div className="text-center text-muted py-5">No permissions found.</div>
 )}
 </div>
 </>
 ) : (
 <div className="text-center text-muted py-5">Select a user to manage permissions.</div>
 )}
 </main>
 </div>

 <style>{`
 .access-page {
 display: flex;
 flex-direction: column;
 gap: 16px;
 }
 .access-toolbar,
 .user-panel,
 .permission-panel {
 background: #fff;
 border: 1px solid #edf1f7;
 border-radius: 8px;
 box-shadow: 0 8px 20px rgba(25, 42, 70, 0.04);
 }
 .access-toolbar {
 display: flex;
 align-items: center;
 justify-content: space-between;
 gap: 16px;
 padding: 18px 20px;
 }
 .access-layout {
 display: grid;
 grid-template-columns: 320px minmax(0, 1fr);
 gap: 16px;
 }
 .user-panel {
 padding: 16px;
 min-height: 640px;
 }
 .user-list {
 display: flex;
 flex-direction: column;
 gap: 8px;
 max-height: 575px;
 overflow: auto;
 padding-right: 4px;
 }
 .user-item {
 width: 100%;
 border: 1px solid #edf1f7;
 background: #fff;
 border-radius: 8px;
 display: flex;
 align-items: center;
 gap: 10px;
 padding: 10px;
 text-align: left;
 transition: all 0.15s ease;
 }
 .user-item:hover,
 .user-item.active {
 border-color: #206fc9;
 background: #f4f8fd;
 }
 .user-avatar,
 .selected-avatar {
 width: 36px;
 height: 36px;
 border-radius: 8px;
 background: #eef6ff;
 color: #206fc9;
 display: inline-flex;
 align-items: center;
 justify-content: center;
 font-weight: 700;
 flex: 0 0 auto;
 }
 .selected-avatar {
 width: 46px;
 height: 46px;
 font-size: 18px;
 }
 .user-copy {
 min-width: 0;
 flex: 1;
 display: flex;
 flex-direction: column;
 }
 .user-name {
 color: #253247;
 font-size: 13px;
 font-weight: 700;
 white-space: nowrap;
 overflow: hidden;
 text-overflow: ellipsis;
 }
 .user-meta {
 color: #7f8c8d;
 font-size: 11.5px;
 white-space: nowrap;
 overflow: hidden;
 text-overflow: ellipsis;
 }
 .status-dot {
 width: 9px;
 height: 9px;
 border-radius: 50%;
 flex: 0 0 auto;
 }
 .status-dot.on { background: #1f9254; }
 .status-dot.off { background: #d9534f; }
 .permission-panel {
 min-width: 0;
 padding: 18px;
 }
 .selected-user-header,
 .permission-filter-row {
 display: flex;
 align-items: center;
 justify-content: space-between;
 gap: 16px;
 border-bottom: 1px solid #edf1f7;
 padding-bottom: 16px;
 margin-bottom: 16px;
 }
 .selected-user-header {
 min-height: 112px;
 }
 .selected-user-info {
 display: grid;
 grid-template-columns: 46px minmax(0, 1fr);
 align-items: center;
 gap: 16px;
 min-width: 260px;
 }
 .selected-user-copy {
 min-width: 0;
 }
 .selected-badges {
 display: flex;
 align-items: center;
 flex-wrap: wrap;
 gap: 8px;
 }
 .selected-actions {
 display: flex;
 align-items: center;
 justify-content: flex-end;
 gap: 14px;
 flex-wrap: wrap;
 }
 .permission-filter-row {
 align-items: stretch;
 }
 .permission-filter-row .input-group {
 flex: 1 1 420px;
 max-width: none;
 }
 .access-badge {
 border: 1px solid #d9e2ef;
 background: #f8faff;
 color: #536274;
 border-radius: 6px;
 padding: 4px 8px;
 font-size: 11.5px;
 font-weight: 700;
 }
 .access-badge.primary {
 border-color: #cfe0f5;
 background: #eef6ff;
 color: #206fc9;
 }
 .access-badge.warning {
 border-color: #ffe0a3;
 background: #fff8e8;
 color: #9a6500;
 }
 .switch-row {
 display: inline-flex;
 align-items: center;
 gap: 8px;
 color: #253247;
 font-size: 13px;
 font-weight: 700;
 }
 .switch-row input {
 width: 18px;
 height: 18px;
 }
 .risk-note {
 flex: 0 1 325px;
 min-width: 280px;
 border: 1px solid #d7f1df;
 background: #f2fbf5;
 color: #1f9254;
 border-radius: 6px;
 display: flex;
 align-items: center;
 padding: 8px 10px;
 font-size: 12px;
 font-weight: 700;
 }
 .risk-note.warn {
 border-color: #ffe0a3;
 background: #fff8e8;
 color: #9a6500;
 }
 .permission-module-list {
 display: flex;
 flex-direction: column;
 gap: 10px;
 max-height: 520px;
 overflow: auto;
 padding-right: 4px;
 }
 .permission-module {
 display: grid;
 grid-template-columns: minmax(190px, 240px) 1fr;
 gap: 14px;
 align-items: center;
 border: 1px solid #edf1f7;
 border-radius: 8px;
 padding: 12px;
 background: #fff;
 }
 .permission-module:hover {
 border-color: #cfe0f5;
 box-shadow: 0 8px 18px rgba(25, 42, 70, 0.04);
 }
 .module-head {
 display: flex;
 align-items: center;
 gap: 10px;
 }
 .module-toggle {
 width: 30px;
 height: 30px;
 border-radius: 6px;
 border: 1px solid #d9e2ef;
 background: #f8faff;
 color: #7f8c8d;
 display: inline-flex;
 align-items: center;
 justify-content: center;
 flex: 0 0 auto;
 }
 .module-toggle.active {
 background: #206fc9;
 border-color: #206fc9;
 color: #fff;
 }
 .module-toggle.partial {
 background: #fff8e8;
 border-color: #f4bd50;
 color: #9a6500;
 }
 .module-title {
 color: #253247;
 font-weight: 800;
 line-height: 1.2;
 }
 .permission-pills {
 display: flex;
 flex-wrap: wrap;
 justify-content: flex-end;
 gap: 8px;
 }
 .permission-pill {
 min-width: 74px;
 min-height: 32px;
 border-radius: 6px;
 border: 1px solid #d9e2ef;
 background: #fff;
 color: #536274;
 display: inline-flex;
 align-items: center;
 justify-content: center;
 gap: 6px;
 padding: 6px 10px;
 font-size: 12px;
 font-weight: 700;
 }
 .permission-pill:hover:not(:disabled) {
 border-color: #206fc9;
 color: #206fc9;
 }
 .permission-pill.active {
 background: #206fc9;
 border-color: #206fc9;
 color: #fff;
 }
 .permission-pill.sensitive:not(.active) {
 border-color: #ffe0a3;
 color: #9a6500;
 background: #fffaf0;
 }
 .permission-pill.sensitive.active {
 background: #9a6500;
 border-color: #9a6500;
 color: #fff;
 }
 .permission-pill:disabled,
 .module-toggle:disabled {
 cursor: not-allowed;
 opacity: 0.55;
 }
 .inherited-icon {
 font-size: 10px;
 opacity: 0.75;
 }
 .fs-12 { font-size: 12px; }
 .fs-13 { font-size: 13px; }
 .fs-14 { font-size: 14px; }
 .text-dark { color: #253247 !important; }
 .text-muted { color: #7f8c8d !important; }
 .bg-light { background-color: #f8faff !important; }
 .user-list::-webkit-scrollbar,
 .permission-module-list::-webkit-scrollbar {
 width: 6px;
 }
 .user-list::-webkit-scrollbar-thumb,
 .permission-module-list::-webkit-scrollbar-thumb {
 background: #c9d2df;
 border-radius: 10px;
 }
 [data-theme-version="dark"] .access-toolbar,
 [data-theme-version="dark"] .user-panel,
 [data-theme-version="dark"] .permission-panel {
 background: #1f2937;
 border-color: #334155;
 box-shadow: none;
 }
 [data-theme-version="dark"] .access-page .text-dark,
 [data-theme-version="dark"] .access-page h4,
 [data-theme-version="dark"] .access-page h5,
 [data-theme-version="dark"] .user-name,
 [data-theme-version="dark"] .module-title,
 [data-theme-version="dark"] .switch-row {
 color: #f8fafc !important;
 }
 [data-theme-version="dark"] .access-page .text-muted,
 [data-theme-version="dark"] .user-meta {
 color: #94a3b8 !important;
 }
 [data-theme-version="dark"] .access-page .input-group-text,
 [data-theme-version="dark"] .access-page .form-control,
 [data-theme-version="dark"] .access-page .bg-light {
 background-color: #111827 !important;
 border-color: #334155 !important;
 color: #e5e7eb !important;
 }
 [data-theme-version="dark"] .access-page .form-control::placeholder {
 color: #6b7280 !important;
 }
 [data-theme-version="dark"] .user-item,
 [data-theme-version="dark"] .permission-module {
 background: #111827;
 border-color: #334155;
 }
 [data-theme-version="dark"] .user-item:hover,
 [data-theme-version="dark"] .user-item.active,
 [data-theme-version="dark"] .permission-module:hover {
 background: #172033;
 border-color: #38bdf8;
 }
 [data-theme-version="dark"] .user-avatar,
 [data-theme-version="dark"] .selected-avatar {
 background: #0f2743;
 color: #7dd3fc;
 }
 [data-theme-version="dark"] .selected-user-header,
 [data-theme-version="dark"] .permission-filter-row {
 border-bottom-color: #334155;
 }
 [data-theme-version="dark"] .access-badge {
 background: #111827;
 border-color: #334155;
 color: #cbd5e1;
 }
 [data-theme-version="dark"] .access-badge.primary {
 background: #0f2743;
 border-color: #1d4ed8;
 color: #93c5fd;
 }
 [data-theme-version="dark"] .access-badge.warning,
 [data-theme-version="dark"] .risk-note.warn {
 background: #3b2a0b;
 border-color: #b7791f;
 color: #facc15;
 }
 [data-theme-version="dark"] .risk-note {
 background: #0f2f23;
 border-color: #166534;
 color: #86efac;
 }
 [data-theme-version="dark"] .module-toggle {
 background: #111827;
 border-color: #475569;
 color: #cbd5e1;
 }
 [data-theme-version="dark"] .module-toggle.active {
 background: #0284c7;
 border-color: #38bdf8;
 color: #fff;
 }
 [data-theme-version="dark"] .module-toggle.partial {
 background: #3b2a0b;
 border-color: #b7791f;
 color: #facc15;
 }
 [data-theme-version="dark"] .permission-pill {
 background: #111827;
 border-color: #475569;
 color: #dbeafe;
 }
 [data-theme-version="dark"] .permission-pill:hover:not(:disabled) {
 border-color: #38bdf8;
 color: #7dd3fc;
 }
 [data-theme-version="dark"] .permission-pill.active {
 background: #0284c7;
 border-color: #38bdf8;
 color: #fff;
 }
 [data-theme-version="dark"] .permission-pill.sensitive:not(.active) {
 background: #3b2a0b;
 border-color: #b7791f;
 color: #facc15;
 }
 [data-theme-version="dark"] .permission-pill.sensitive.active {
 background: #b45309;
 border-color: #f59e0b;
 color: #fff7ed;
 }
 [data-theme-version="dark"] .selected-actions .btn {
 background: transparent;
 }
 [data-theme-version="dark"] .selected-actions .btn-outline-secondary {
 background: rgba(15, 23, 42, 0.25);
 }
 [data-theme-version="dark"] .permission-filter-row .input-group-text {
 min-width: 54px;
 justify-content: center;
 }
 [data-theme-version="dark"] .permission-filter-row .form-control {
 min-height: 44px;
 }
 [data-theme-version="dark"] .risk-note.warn {
 min-height: 44px;
 }
 [data-theme-version="dark"] .status-dot.on {
 background: #22c55e;
 box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.14);
 }
 [data-theme-version="dark"] .status-dot.off {
 background: #ef4444;
 box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.14);
 }
 [data-theme-version="dark"] .access-page .btn-outline-primary {
 color: #7dd3fc;
 border-color: #38bdf8;
 }
 [data-theme-version="dark"] .access-page .btn-outline-primary:hover {
 background: #0284c7;
 color: #fff;
 }
 [data-theme-version="dark"] .access-page .btn-outline-secondary {
 color: #e2e8f0;
 border-color: #64748b;
 }
 [data-theme-version="dark"] .access-page .btn-outline-secondary:hover {
 background: #334155;
 color: #fff;
 }
 [data-theme-version="dark"] .user-list::-webkit-scrollbar-thumb,
 [data-theme-version="dark"] .permission-module-list::-webkit-scrollbar-thumb {
 background: #475569;
 }
 @media (max-width: 992px) {
 .access-layout {
 grid-template-columns: 1fr;
 }
 .user-panel {
 min-height: auto;
 }
 .user-list {
 max-height: 280px;
 }
 .selected-user-header,
 .permission-filter-row,
 .access-toolbar {
 align-items: flex-start;
 flex-direction: column;
 }
 .selected-actions {
 justify-content: flex-start;
 width: 100%;
 }
 .permission-filter-row .input-group,
 .risk-note {
 width: 100%;
 max-width: none;
 flex-basis: auto;
 }
 .permission-module {
 grid-template-columns: 1fr;
 }
 .permission-pills {
 justify-content: flex-start;
 }
 }
 @media (max-width: 576px) {
 .selected-user-info {
 grid-template-columns: 40px minmax(0, 1fr);
 gap: 12px;
 min-width: 0;
 width: 100%;
 }
 .selected-avatar {
 width: 40px;
 height: 40px;
 }
 .selected-actions .btn {
 flex: 1 1 130px;
 }
 }
 `}</style>
 </div>
 );
};

export default RolePermissions;
